import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Building2, Plus, Check, ChevronDown, FolderPlus } from "lucide-react";

/**
 * PropertyPicker — lets the user select a "Kohde" (property) inside a
 * Pääryhmä (group). If needed, they can create a new group + new property.
 *
 * Props:
 *  - value: property_id | null
 *  - onChange(property_id | null)
 *  - label?: string (defaults to the localized "Property (optional)")
 */
export default function PropertyPicker({ value, onChange, label }) {
  const { t } = useT();
  const finalLabel = label ?? t("pp.label");
  const [groups, setGroups] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("pick"); // pick | new
  const [newPropName, setNewPropName] = useState("");
  const [newPropGroup, setNewPropGroup] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: p }] = await Promise.all([
        api.get("/property-groups"),
        api.get("/properties"),
      ]);
      setGroups(g.groups || []);
      setProperties(p.properties || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const selected = properties.find((p) => p.property_id === value);

  const createProperty = async () => {
    if (!newPropName.trim()) { toast.error(t("pp.nameRequired")); return; }
    setCreating(true);
    try {
      let groupId = newPropGroup;
      if (newPropGroup === "__new__") {
        if (!newGroupName.trim()) { toast.error(t("pp.groupNameRequired")); setCreating(false); return; }
        const { data: g } = await api.post("/property-groups", { name: newGroupName.trim() });
        groupId = g.group_id;
      }
      if (!groupId) {
        if (groups.length === 0) {
          const { data: g } = await api.post("/property-groups", { name: t("pp.unfiledGroup") });
          groupId = g.group_id;
        } else {
          groupId = groups[0].group_id;
        }
      }
      const { data: p } = await api.post("/properties", { name: newPropName.trim(), group_id: groupId });
      await load();
      onChange(p.property_id);
      setMode("pick");
      setNewPropName("");
      setNewPropGroup("");
      setNewGroupName("");
      toast.success(`${t("pp.createdToast")}: "${p.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pp.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  // Group properties by group_id for nice display.
  // Piilota ryhmät, joissa ei ole yhtään kohdetta — vanhojen orpo-ryhmien
  // näyttäminen ("Ei kohteita") sekoittaa käyttäjää siirtodialogissa.
  const groupsById = Object.fromEntries(groups.map((g) => [g.group_id, g]));
  const byGroup = groups
    .map((g) => ({
      group: g,
      items: properties.filter((p) => p.group_id === g.group_id),
    }))
    .filter(({ items }) => items.length > 0);

  return (
    <div data-testid="property-picker">
      {finalLabel && <label className="block text-sm font-medium text-slate-700 mb-1.5">{finalLabel}</label>}

      {mode === "pick" && (
        <>
          <div className="border border-slate-200 rounded-xl bg-white max-h-[340px] overflow-y-auto p-2">
            <button
              type="button"
              onClick={() => onChange(null)}
              className={`w-full text-left rounded-lg px-2 py-1.5 text-sm flex items-center gap-2 ${value == null ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-slate-100"}`}
              data-testid="property-picker-none"
            >
              <span className="h-4 w-4 inline-flex items-center justify-center text-slate-400">—</span>
              {t("pp.none")}
              {value == null && <Check className="h-3.5 w-3.5 ml-auto text-emerald-600" />}
            </button>
            {loading && <div className="text-xs text-slate-400 px-2 py-3">{t("pp.loading")}</div>}
            {!loading && groups.length === 0 && (
              <div className="text-xs text-slate-500 px-2 py-3 italic">{t("pp.noGroupsYet")}</div>
            )}
            {byGroup.map(({ group, items }) => (
              <div key={group.group_id} className="mt-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 py-1 flex items-center gap-1.5">
                  <Building2 className="h-3 w-3" />
                  {group.name}
                  <span className="text-slate-400">({items.length})</span>
                </div>
                {items.length === 0 && (
                  <div className="text-[11px] text-slate-400 px-3 py-1 italic">{t("pp.noProps")}</div>
                )}
                {items.map((p) => (
                  <button
                    key={p.property_id}
                    type="button"
                    onClick={() => onChange(p.property_id)}
                    className={`w-full text-left rounded-lg px-3 py-1.5 text-sm flex items-center gap-2 ${value === p.property_id ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-slate-100"}`}
                    data-testid={`property-picker-option-${p.property_id}`}
                  >
                    <Building2 className="h-4 w-4 text-slate-400" />
                    <span className="flex-1 truncate">{p.name}</span>
                    {value === p.property_id && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                  </button>
                ))}
              </div>
            ))}
          </div>
          {selected && (
            <div className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              {t("pp.selected")}: <strong>{selected.name}</strong>
              <span className="text-slate-400">· {groupsById[selected.group_id]?.name}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setMode("new")}
            className="mt-2 text-sm text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5 font-semibold"
            data-testid="property-picker-new"
          >
            <Plus className="h-3.5 w-3.5" /> {t("pp.createNew")}
          </button>
        </>
      )}

      {mode === "new" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3" data-testid="property-picker-new-form">
          <input
            autoFocus
            value={newPropName}
            onChange={(e) => setNewPropName(e.target.value)}
            placeholder={t("pp.propNamePh")}
            className="input-base"
            data-testid="property-picker-new-name"
          />
          <div>
            <div className="text-xs text-slate-500 mb-1">{t("pp.group")}</div>
            <div className="relative">
              <select
                value={newPropGroup}
                onChange={(e) => setNewPropGroup(e.target.value)}
                className="input-base appearance-none pr-8"
                data-testid="property-picker-new-group"
              >
                <option value="">{t("pp.pickGroup")}</option>
                {groups.map((g) => (
                  <option key={g.group_id} value={g.group_id}>{g.name}</option>
                ))}
                <option value="__new__">{t("pp.newGroup")}</option>
              </select>
              <ChevronDown className="h-4 w-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
          {newPropGroup === "__new__" && (
            <input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder={t("pp.newGroupPh")}
              className="input-base"
              data-testid="property-picker-new-group-name"
            />
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setMode("pick")} className="btn-ghost text-sm">{t("pp.cancel")}</button>
            <button type="button" onClick={createProperty} disabled={creating || !newPropName.trim()} className="btn-primary text-sm disabled:opacity-50" data-testid="property-picker-new-save">
              <FolderPlus className="h-4 w-4" /> {creating ? t("pp.creating") : t("pp.create")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
